import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faReceipt } from '@/icons';
import { useTheme } from '../contexts/ThemeContext';

export interface ClientEditingIndicatorProps {
    isVisible: boolean;
}

/**
 * ConcurrentCaseEditGuard A2: a non-blocking pill shown to the attorney in the wizard when a
 * link-token client is currently active in the expense portal for this same case (detected by
 * the wizard's presence poll -- see useCaseEditingLock's onClientEditingChange). Informational
 * only; `pointer-events: none` so it never intercepts clicks. Auto-hides when the client's
 * presence lease lapses (~60s TTL). See docs/AUTOSAVE_DETAILS.md §17.
 */
export const ClientEditingIndicator: React.FC<ClientEditingIndicatorProps> = ({ isVisible }) => {
    const theme = useTheme();

    if (!isVisible) {
        return null;
    }

    return (
        <div
            style={{
                position: 'fixed',
                top: theme.spacing.md,
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 9000,
                pointerEvents: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: theme.spacing.sm,
                padding: `${theme.spacing.xs} ${theme.spacing.md}`,
                backgroundColor: '#eff6ff',
                border: `1px solid ${theme.colors.primary}`,
                borderRadius: theme.borderRadius.lg,
                boxShadow: theme.shadows?.md || '0 4px 12px rgba(0,0,0,0.12)',
                color: '#1e3a8a',
                fontSize: theme.typography.fontSize.sm,
                fontWeight: theme.typography.fontWeight.medium,
                maxWidth: '90vw',
                whiteSpace: 'nowrap',
            }}
            role="status"
            aria-live="polite"
        >
            <FontAwesomeIcon icon={faReceipt} />
            <span>A client is currently entering expenses for this case</span>
        </div>
    );
};
